import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as userSessionActions from '../../redux/actions/user-session'
import * as breakWorksActions from "../../redux/actions/breakworks";

import Api from "../../services/api";

import * as AgentWorkspaceService from '../../services/agentworkspace.service';

import { Avatar, Grid, Stack, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import Title from "../../components/title-name";
import RealSimpleAutoComplete from "../../components/auto-complete/autocomplete-with-onclick";

import { formatIntervalToReadable } from "src/utils/breakReason";

import { Timer as TimerIcon } from "@mui/icons-material";
import { PapiroConsole } from "../../utils/papiroConsole";

const defaultState = {
	loading: false,
	allBreakReasons: [],
	loadingAllBreakReasons: false,
	reasonBreakId: null,
};

const StartBreakReasonForm = forwardRef(({ classes, onClose, setState, ...props }, ref) => {
	const intl = useIntl();
	const formRef = useRef(null);

	const user = props.userSession.user;

	const [internalState, setInternalState] = useState(defaultState);

	const getAllBreakReasonsList = useCallback(async () => {
		try {
			let url = `/break-reason/get-all`;
			const result = await Api.get(url);
			return result.data
		} catch (error) {
			throw error;
		}
	}, [])

	const fetchAllBreakReasons = useCallback(async () => {
		if (internalState.loadingAllBreakReasons || internalState.allBreakReasons.length > 0) return;

		setInternalState(prevState => ({ ...prevState, loadingAllBreakReasons: true }));

		try {
			const response = await getAllBreakReasonsList();

			if (response.success) {
				const data = response.data;

				const formattedData = data.map(item => {
					const nameValue = item.name ? item.name : '';
					const breakLimitValue = item.breaklimit ? ` - ${formatIntervalToReadable(item.breaklimit)}` : '';

					const name = `${nameValue}${breakLimitValue}`;
					return {
						...item,
						name: name,
					}
				});

				setInternalState(prevState => ({
					...prevState,
					loadingAllBreakReasons: false,
					allBreakReasons: formattedData,
				}));
			} else {
				setState({
					loading: false,
					openNotification: true,
					notificationVariant: "error",
					notificationMessage:
						response.data && response.data.errors
							? response.data.errors[0]
							: intl.formatMessage({ id: "process.error" }),
				});
			}
		} catch (err) {
			setState({
				loading: false,
				openNotification: true,
				notificationVariant: "error",
				notificationMessage:
					err.response && err.response.data && err.response.data.errors
						? err.response.data.errors[0]
						: intl.formatMessage({ id: "process.error" }),
			});

		} finally {
			setInternalState(prevState => ({ ...prevState, loadingAllBreakReasons: false }));
		}
	}, [getAllBreakReasonsList, internalState.allBreakReasons.length, internalState.loadingAllBreakReasons]);

	async function fetchConfigLockSystemDuringBreakWork() {
		try {
			const response = await Api.get("configs/locksystemduringbreakwork");

			if (response?.data?.success) {
				const lockConfig = response.data?.data?.locksystemduringbreakwork ?? false;
				props.updateLockSystemDuringBreakWork(lockConfig);
			} else {
				PapiroConsole.log("DialogActiveBreakWork - Failed to fetch locksystemduringbreakwork config.", response);
			}
		} catch (err) {
			PapiroConsole.log("DialogActiveBreakWork - Erro ao buscar configuração locksystemduringbreakwork:", err);
		}
	}

	async function handleSubmit(e) {
		try {
			if (e) e.preventDefault();

			{ !!onClose && onClose() }

			setState({ loading: true });

			let payload = new FormData();

			for (let key in internalState) {
				payload.append(key, internalState[key]);
			}

			payload.append("login", user?.email);

			let config = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};

			const result = await Api.post("/break-work", payload, config)
			if (result.data.success) {
				const breakWorkData = result.data.data;

				await fetchConfigLockSystemDuringBreakWork(); // Busca a configuração atualizada de locksystemduringbreakwork

				props.addBreakReasonActive(breakWorkData); // Salva o motivo de pausa ativo no Redux

				await AgentWorkspaceService.updateStatus(3); // Atualiza o status do agente para "Pausado"

				setState({
					loading: false,
					openNotification: true,
					notificationVariant: "success",
					notificationMessage: intl.formatMessage({ id: "break.reason.start.successfully" })
				});
			} else {
				setState({
					loading: false,
					openNotification: true,
					notificationVariant: "error",
					notificationMessage:
						result.data && result.data.errors
							? result.data.errors[0]
							: intl.formatMessage({ id: "process.error" }),
				});
			}


		} catch (err) {
			setState({
				loading: false,
				openNotification: true,
				notificationVariant: "error",
				notificationMessage:
					err.response && err.response.data && err.response.data.errors
						? err.response.data.errors[0]
						: intl.formatMessage({ id: "process.error" }),
			});
		}
	}

	useImperativeHandle(ref, () => ({
		submitForm: () => {
			return new Promise((resolve) => {
				if (formRef.current) {
					const isValid = formRef.current.reportValidity();
					if (isValid) {
						formRef.current.requestSubmit();
						resolve(true);
					} else {
						resolve(false);
					}
				} else {
					resolve(false);
				}
			});
		},
	}), []);

	return (
		<Stack
			flex={1}
			alignItems="center"
			padding={4}
			spacing={2}
		>
			{!!!onClose &&
				<>
					<Avatar className={classes.avatar}>
						<TimerIcon />
					</Avatar>

					<Typography component="h1" variant="h5" >
						{<FormattedMessage id="menu.register.break" />} <Title />
					</Typography>
				</>
			}

			<form
				ref={formRef}
				name="myForm"
				style={{ width: "100%" }}
				onSubmit={handleSubmit}
				encType="application/json"
			>
				<Grid container spacing={2} >
					<Grid item xs={12}>
						<RealSimpleAutoComplete
							required
							style={{
								background: "white",
								fullWidth: true,
							}}
							label={intl.formatMessage({ id: "break.reason.form" })}
							options={internalState.allBreakReasons}
							stateName="allBreakReasons"
							changeSelect={(
								stateName,
								value
							) => {
								setInternalState(prevState => ({
									...prevState,
									reasonBreakId: value ? value.id : null,
									breakLimit: value ? value.breaklimit : null,
								}));
							}}
							onFocus={fetchAllBreakReasons}
							loading={internalState.loadingAllBreakReasons}
							selected={internalState.allBreakReasons.find(
								(item) =>
									item.id === internalState.reasonBreakId
							)}
						/>
					</Grid>
				</Grid>
			</form>
		</Stack>
	);
})

const mapStateToProps = (state) => ({
	userSession: state.userSession,
});

const mapDispatchToProps = dispatch =>
	bindActionCreators(Object.assign({}, breakWorksActions, userSessionActions), dispatch)

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(StartBreakReasonForm);
